<script setup>
import { ref, nextTick } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import ActionBlock from './ActionBlock.vue';
import FilterBlock from './FilterBlock.vue';
import AutomationSelect from './AutomationSelect.vue';
import AutomationLogPanel from './AutomationLogPanel.vue';

const props = defineProps({
  pipelineId: { type: [Number, String], required: true },
  columnId: { type: [Number, String], required: true },
  automation: { type: Object, default: null },
  // tipo de ação escolhido no catálogo ao criar uma nova automação
  initialActionType: { type: String, default: 'add_tag' },
});
const emit = defineEmits(['saved', 'cancel', 'delete']);

const store = useStore();
const { t } = useI18n();

const UNIT_OPTIONS = [
  { value: 'minutes', label: t('KANBAN.AUTOMATIONS.PANEL.UNIT_MINUTES') },
  { value: 'hours', label: t('KANBAN.AUTOMATIONS.PANEL.UNIT_HOURS') },
  { value: 'days', label: t('KANBAN.AUTOMATIONS.PANEL.UNIT_DAYS') },
  { value: 'months', label: t('KANBAN.AUTOMATIONS.PANEL.UNIT_MONTHS') },
  { value: 'years', label: t('KANBAN.AUTOMATIONS.PANEL.UNIT_YEARS') },
];

const RECURRENCE_OPTIONS = [
  { value: 'none', label: t('KANBAN.AUTOMATIONS.PANEL.RECURRENCE_NONE') },
  { value: 'daily', label: t('TASKS.RECURRENCE.DAILY') },
  { value: 'weekdays', label: t('TASKS.RECURRENCE.WEEKDAYS') },
  { value: 'weekly', label: t('TASKS.RECURRENCE.WEEKLY') },
  { value: 'monthly', label: t('TASKS.RECURRENCE.MONTHLY') },
  { value: 'custom', label: t('TASKS.RECURRENCE.CUSTOM') },
];

const existingSchedule = props.automation?.trigger_config?.schedule || {};

const form = ref({
  actions: props.automation?.actions?.length
    ? props.automation.actions
    : [{ type: props.initialActionType, config: {} }],
  filters: props.automation?.filters || [],
  filter_operator: props.automation?.filter_operator || 'and',
  run_once_per_card: props.automation?.run_once_per_card ?? false,
  active: props.automation?.active ?? true,
  schedule: {
    value: existingSchedule.value ?? 0,
    unit: existingSchedule.unit ?? 'minutes',
    recurrence: existingSchedule.recurrence ?? 'none',
    business_hours_only: existingSchedule.business_hours_only ?? false,
  },
});

const showConditions = ref((props.automation?.filters || []).length > 0);
const isSaving = ref(false);
const confirmingDelete = ref(false);
// fica fechado na carga inicial mesmo em automação recorrente já salva — o
// painel é um popover sobre o formulário; abre ao trocar a recorrência.
const showCustomRecurrence = ref(false);

const CUSTOM_UNIT_OPTIONS = [
  { key: 'days', label: t('TASKS.RECURRENCE.UNITS.DAYS') },
  { key: 'weeks', label: t('TASKS.RECURRENCE.UNITS.WEEKS') },
  { key: 'months', label: t('TASKS.RECURRENCE.UNITS.MONTHS') },
];

const customInterval = ref(existingSchedule.recurrence_interval ?? 1);
const customUnit = ref(existingSchedule.recurrence_unit ?? 'days');
const customEndsMode = ref(existingSchedule.recurrence_ends_mode ?? 'never');
const customEndsAt = ref(existingSchedule.recurrence_ends_at ?? '');
const customEndsCount = ref(existingSchedule.recurrence_ends_count ?? 1);

function onRecurrenceChange(val) {
  form.value.schedule.recurrence = val;
  // qualquer recorrência precisa do painel para configurar o término; só a
  // custom mostra também o seletor de intervalo/unidade.
  showCustomRecurrence.value = val !== 'none';
}

function confirmCustom() {
  showCustomRecurrence.value = false;
}

// Critério de término efetivamente aplicável. Um modo escolhido sem o valor
// que o acompanha (data em branco, contagem vazia/zerada) é gravado como
// 'never': o backend descarta um recurrence_ends_at vazio via .presence e um
// recurrence_ends_count não positivo, caindo no ramo "never" de
// recurrence_finished? — a automação reexecutaria para sempre, com a UI
// mostrando um término configurado. Melhor persistir o que de fato vale.
function resolvedEndsCriteria() {
  const endsAt = customEndsAt.value;
  if (customEndsMode.value === 'on_date' && endsAt) {
    return {
      recurrence_ends_mode: 'on_date',
      recurrence_ends_at: endsAt,
      recurrence_ends_count: null,
    };
  }

  const endsCount = Number(customEndsCount.value);
  if (customEndsMode.value === 'after' && endsCount > 0) {
    return {
      recurrence_ends_mode: 'after',
      recurrence_ends_at: null,
      recurrence_ends_count: endsCount,
    };
  }

  return {
    recurrence_ends_mode: 'never',
    recurrence_ends_at: null,
    recurrence_ends_count: null,
  };
}

const defaultName = () => {
  const type = form.value.actions[0]?.type || props.initialActionType;
  return t(`KANBAN.AUTOMATIONS.ACTIONS.${type.toUpperCase()}`);
};

const automationName = ref(props.automation?.name || defaultName());
const isEditingName = ref(false);
const nameInputRef = ref(null);

function startEditingName() {
  isEditingName.value = true;
  nextTick(() => nameInputRef.value?.select());
}

function commitName() {
  isEditingName.value = false;
  if (!automationName.value.trim()) automationName.value = defaultName();
}

async function save() {
  isSaving.value = true;
  try {
    const payload = {
      pipeline_id: props.pipelineId,
      column_id: props.columnId,
      name: automationName.value.trim() || defaultName(),
      trigger_type: 'card_moved',
      trigger_config: {
        schedule: {
          ...form.value.schedule,
          // o critério de término vale para qualquer recorrência (daily,
          // weekly, monthly...), não só a custom — sem isso o backend cai no
          // ramo "never" de recurrence_finished? e reexecuta indefinidamente.
          ...(form.value.schedule.recurrence !== 'none' &&
            resolvedEndsCriteria()),
          // intervalo/unidade só existem no modo custom; os demais tipos têm
          // intervalo fixo derivado do próprio nome da recorrência.
          ...(form.value.schedule.recurrence === 'custom' && {
            recurrence_interval: customInterval.value,
            recurrence_unit: customUnit.value,
          }),
        },
      },
      filters: form.value.filters,
      filter_operator: form.value.filter_operator,
      actions: form.value.actions,
      run_once_per_card: form.value.run_once_per_card,
      active: form.value.active,
    };
    if (props.automation?.id) {
      await store.dispatch('kanbanAutomations/update', {
        id: props.automation.id,
        ...payload,
      });
    } else {
      await store.dispatch('kanbanAutomations/create', payload);
    }
    emit('saved');
  } finally {
    isSaving.value = false;
  }
}

const scheduleRowStyle = { alignItems: 'center' };
const scheduleInputStyle = {
  alignSelf: 'center',
  marginTop: 'auto',
  marginBottom: 'auto',
};
</script>

<template>
  <div
    class="flex flex-col gap-3 rounded-lg border border-n-weak bg-n-alpha-1 p-3"
  >
    <!-- Nome da automação -->
    <div class="pb-2 border-b border-n-weak">
      <p class="text-[10px] text-n-slate-10 uppercase tracking-wide mb-0.5">
        {{ $t('KANBAN.AUTOMATIONS.PANEL.NAME_LABEL') }}
      </p>
      <input
        ref="nameInputRef"
        v-model="automationName"
        type="text"
        :readonly="!isEditingName"
        :placeholder="defaultName()"
        class="block w-full text-sm font-semibold text-n-slate-12 !bg-transparent !outline-none !shadow-none !p-0 !m-0 !h-auto !rounded-none transition-colors"
        :class="
          isEditingName
            ? '!border-t-0 !border-l-0 !border-r-0 border-b border-n-weak cursor-text'
            : '!border-none truncate cursor-pointer hover:text-n-brand'
        "
        @blur="commitName"
        @keydown.enter="commitName"
        @keydown.escape="
          isEditingName = false;
          automationName = automation?.name || defaultName();
        "
        @click="!isEditingName && startEditingName()"
      />
    </div>

    <ActionBlock
      v-model:actions="form.actions"
      single
      :pipeline-id="pipelineId"
    />

    <!-- Agendamento -->
    <div
      class="relative flex flex-col gap-2 rounded-lg border border-n-weak bg-n-background p-2.5"
    >
      <div class="flex items-center gap-2 text-sm font-medium text-n-slate-12">
        <span class="i-lucide-clock size-4 text-n-slate-10" />
        {{ $t('KANBAN.AUTOMATIONS.PANEL.SCHEDULING') }}
      </div>
      <div class="flex gap-2" :style="scheduleRowStyle">
        <input
          v-model.number="form.schedule.value"
          type="number"
          min="0"
          class="reset-base !w-16 !h-8 shrink-0 grow-0 rounded-lg border border-n-weak bg-n-surface-2 pl-2 pr-1 text-sm text-n-slate-12 focus:outline-none appearance-auto"
          :style="scheduleInputStyle"
        />
        <AutomationSelect
          v-model="form.schedule.unit"
          compact
          :options="UNIT_OPTIONS"
        />
        <div>
          <AutomationSelect
            compact
            :model-value="form.schedule.recurrence"
            :options="RECURRENCE_OPTIONS"
            @update:model-value="onRecurrenceChange"
          />
        </div>
      </div>
      <!-- Modal de recorrência personalizada — posicionado dentro do bloco de agendamento -->
      <div
        v-if="showCustomRecurrence"
        class="absolute left-0 right-0 top-full mt-1 z-30 bg-n-background border border-n-weak rounded-xl shadow-xl overflow-hidden"
        @click.stop
      >
        <!-- Repetir a cada — só na recorrência personalizada; os demais tipos
             têm intervalo fixo derivado do próprio nome da recorrência -->
        <div
          v-if="form.schedule.recurrence === 'custom'"
          class="px-4 py-3 border-b border-n-weak"
        >
          <p class="text-xs text-n-slate-10 mb-2">
            {{ $t('TASKS.RECURRENCE.REPEAT_EVERY') }}
          </p>
          <div class="flex items-center gap-2">
            <div
              class="flex items-center border border-n-weak rounded-lg overflow-hidden bg-n-surface-2 w-24"
            >
              <button
                type="button"
                class="px-2.5 py-2 text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-1 text-sm leading-none"
                @click="customInterval = Math.max(1, customInterval - 1)"
              >
                −
              </button>
              <span
                class="flex-1 text-center text-sm font-medium text-n-slate-12 tabular-nums"
              >
                {{ customInterval }}
              </span>
              <button
                type="button"
                class="px-2.5 py-2 text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-1 text-sm leading-none"
                @click="customInterval++"
              >
                +
              </button>
            </div>
            <div class="flex gap-1 flex-wrap">
              <button
                v-for="u in CUSTOM_UNIT_OPTIONS"
                :key="u.key"
                type="button"
                class="px-2.5 py-1.5 text-sm rounded-lg border transition-colors"
                :class="
                  customUnit === u.key
                    ? 'border-n-brand text-n-brand bg-n-alpha-2'
                    : 'border-n-weak text-n-slate-11 hover:bg-n-alpha-1'
                "
                @click="customUnit = u.key"
              >
                {{ u.label }}
              </button>
            </div>
          </div>
        </div>
        <!-- Termina em — última seção do painel, sem border-b para não deixar
             uma linha solta no rodapé quando é a única seção visível -->
        <div class="px-4 py-3">
          <p class="text-xs text-n-slate-10 mb-2">
            {{ $t('TASKS.RECURRENCE.ENDS') }}
          </p>
          <div class="flex flex-col gap-1">
            <button
              type="button"
              class="flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors"
              :class="
                customEndsMode === 'never'
                  ? 'bg-n-alpha-2 text-n-brand'
                  : 'text-n-slate-12 hover:bg-n-alpha-1'
              "
              @click="customEndsMode = 'never'"
            >
              <span
                class="size-4 rounded-full border-2 flex items-center justify-center flex-shrink-0"
                :class="
                  customEndsMode === 'never'
                    ? 'border-n-brand'
                    : 'border-n-weak'
                "
              >
                <span
                  v-if="customEndsMode === 'never'"
                  class="size-2 rounded-full bg-n-brand"
                />
              </span>
              {{ $t('TASKS.RECURRENCE.NEVER') }}
            </button>
            <button
              type="button"
              class="flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors"
              :class="
                customEndsMode === 'on_date'
                  ? 'bg-n-alpha-2 text-n-brand'
                  : 'text-n-slate-12 hover:bg-n-alpha-1'
              "
              @click="customEndsMode = 'on_date'"
            >
              <span
                class="size-4 rounded-full border-2 flex items-center justify-center flex-shrink-0"
                :class="
                  customEndsMode === 'on_date'
                    ? 'border-n-brand'
                    : 'border-n-weak'
                "
              >
                <span
                  v-if="customEndsMode === 'on_date'"
                  class="size-2 rounded-full bg-n-brand"
                />
              </span>
              <span class="flex-shrink-0">{{
                $t('TASKS.RECURRENCE.ON_DATE')
              }}</span>
              <input
                v-model="customEndsAt"
                type="date"
                :disabled="customEndsMode !== 'on_date'"
                class="ml-auto text-sm bg-n-surface-2 border border-n-weak rounded-lg px-2 py-1 outline-none focus:border-n-brand disabled:opacity-40 text-n-slate-12"
                @click.stop
              />
            </button>
            <button
              type="button"
              class="flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors"
              :class="
                customEndsMode === 'after'
                  ? 'bg-n-alpha-2 text-n-brand'
                  : 'text-n-slate-12 hover:bg-n-alpha-1'
              "
              @click="customEndsMode = 'after'"
            >
              <span
                class="size-4 rounded-full border-2 flex items-center justify-center flex-shrink-0"
                :class="
                  customEndsMode === 'after'
                    ? 'border-n-brand'
                    : 'border-n-weak'
                "
              >
                <span
                  v-if="customEndsMode === 'after'"
                  class="size-2 rounded-full bg-n-brand"
                />
              </span>
              <span class="flex-shrink-0">{{
                $t('TASKS.RECURRENCE.AFTER')
              }}</span>
              <input
                v-model.number="customEndsCount"
                type="number"
                min="1"
                :disabled="customEndsMode !== 'after'"
                class="reset-base !w-16 !h-8 shrink-0 grow-0 rounded-lg border border-n-weak bg-n-surface-2 pl-2 pr-1 text-sm text-n-slate-12 focus:outline-none appearance-auto disabled:opacity-40"
                @click.stop
              />
              <span class="text-sm">{{
                $t('TASKS.RECURRENCE.OCCURRENCES')
              }}</span>
            </button>
          </div>
        </div>
      </div>
      <!-- overlay para fechar — confirma automaticamente ao clicar fora -->
      <div
        v-if="showCustomRecurrence"
        class="fixed inset-0 z-20"
        @click="confirmCustom"
      />
      <label class="flex items-center gap-2 text-sm text-n-slate-11">
        <input
          v-model="form.schedule.business_hours_only"
          type="checkbox"
          class="rounded accent-n-brand"
        />
        {{ $t('KANBAN.AUTOMATIONS.PANEL.BUSINESS_HOURS') }}
      </label>
      <label class="flex items-center gap-2 text-sm text-n-slate-11">
        <input
          v-model="form.run_once_per_card"
          type="checkbox"
          class="rounded accent-n-brand"
        />
        {{ $t('KANBAN.AUTOMATIONS.PANEL.RUN_ONCE') }}
      </label>
    </div>

    <!-- Configuração Condicional -->
    <div
      class="flex flex-col gap-2 rounded-lg border border-n-weak bg-n-background p-2.5"
    >
      <button
        type="button"
        class="group relative flex items-center justify-between text-sm font-medium text-n-slate-12"
        @click="showConditions = !showConditions"
      >
        <span class="flex items-center gap-2">
          <span class="i-lucide-git-branch size-4 text-n-slate-10" />
          {{ $t('KANBAN.AUTOMATIONS.PANEL.CONDITIONS') }}
        </span>
        <span
          class="size-4 text-n-slate-10"
          :class="
            showConditions ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'
          "
        />
        <span
          class="pointer-events-none absolute left-0 top-full mt-1.5 z-10 w-64 rounded-lg border border-n-weak bg-n-background px-3 py-2 text-xs font-normal text-n-slate-11 shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"
        >
          {{ $t('KANBAN.AUTOMATIONS.PANEL.CONDITIONS_HINT') }}
        </span>
      </button>
      <template v-if="showConditions">
        <FilterBlock
          v-model:filters="form.filters"
          v-model:operator="form.filter_operator"
        />
      </template>
    </div>

    <AutomationLogPanel v-if="automation?.id" :automation-id="automation.id" />

    <div class="flex items-center justify-between border-t border-n-weak pt-3">
      <div v-if="automation?.id" class="relative">
        <button
          type="button"
          class="rounded p-1.5 transition-colors"
          :class="
            confirmingDelete
              ? 'text-n-ruby-11 bg-n-alpha-1'
              : 'text-n-slate-10 hover:text-n-ruby-11 hover:bg-n-alpha-1'
          "
          :aria-label="$t('KANBAN.AUTOMATIONS.PANEL.DELETE')"
          @click="confirmingDelete = !confirmingDelete"
        >
          <span class="i-lucide-trash-2 size-4" />
        </button>
        <div
          v-if="confirmingDelete"
          class="absolute bottom-full left-0 mb-2 z-20 flex items-center gap-2 rounded-lg border border-n-weak bg-n-background px-3 py-2 shadow-lg whitespace-nowrap"
        >
          <span class="text-xs text-n-slate-11">{{
            $t('KANBAN.AUTOMATIONS.PANEL.DELETE_CONFIRM')
          }}</span>
          <button
            type="button"
            class="text-xs font-medium text-n-ruby-11 hover:text-n-ruby-10"
            @click="emit('delete')"
          >
            {{ $t('KANBAN.AUTOMATIONS.PANEL.DELETE_YES') }}
          </button>
          <button
            type="button"
            class="text-xs text-n-slate-11 hover:text-n-slate-12"
            @click="confirmingDelete = false"
          >
            {{ $t('KANBAN.AUTOMATIONS.PANEL.DELETE_NO') }}
          </button>
        </div>
      </div>
      <span v-else />
      <div class="flex items-center gap-2">
        <button
          type="button"
          class="rounded-lg border border-n-weak px-3 py-1.5 text-sm text-n-slate-12 hover:bg-n-alpha-1"
          @click="emit('cancel')"
        >
          {{ $t('KANBAN.AUTOMATIONS.CANCEL') }}
        </button>
        <button
          type="button"
          class="rounded-lg bg-n-brand px-3 py-1.5 text-sm font-medium text-white hover:brightness-110 disabled:opacity-50"
          :disabled="isSaving"
          @click="save"
        >
          {{ $t('KANBAN.AUTOMATIONS.PANEL.SAVE') }}
        </button>
      </div>
    </div>
  </div>
</template>
